/* eslint-disable no-console */
// src/AuthProvider.tsx
import type { OktaAuthOptions, TokenManagerError } from '@okta/okta-auth-js';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { Security } from '@okta/okta-react';
import React, { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SecurityConfig } from './security-utils';
import { sanitizeRedirectUrl, sessionManager } from './security-utils';

export type AuthProviderProps = {
  config: OktaAuthOptions;
  children: React.ReactNode;
  onTokenError?: (error: TokenManagerError) => void;
  onAuthRequired?: (oktaAuth: OktaAuth) => void;
  enableDebugLogs?: boolean;
  loginPath?: string;
  defaultRedirectPath?: string;
  /** Path to redirect after logout (defaults to loginPath) */
  logoutRedirectPath?: string;
  securityConfig?: SecurityConfig;
  onSecurityViolation?: (reason: string) => void;
};

export interface AuthSettingsContextValue {
  loginPath: string;
  logoutRedirectPath: string;
  defaultRedirectPath: string;
}

export const AuthSettingsContext = createContext<AuthSettingsContextValue | undefined>(undefined);

/**
 * Enhanced AuthProvider with better error handling and configuration options
 * Redirect Strategy:
 * - RequireAuth sets oktaAuth.originalUri before navigating user to /login
 * - Login page calls login() WITHOUT overriding originalUri, preserving state
 * - After IdP redirect, <LoginCallback /> or Security.restoreOriginalUri uses that originalUri
 * - Fallback is defaultRedirectPath ("/" by default)
 * No query string '?redirect=' is used; the IdP round-trip state is leaner & less error-prone.
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({
  config,
  children,
  onTokenError,
  onAuthRequired: customOnAuthRequired,
  enableDebugLogs = false,
  loginPath = '/login',
  defaultRedirectPath = '/',
  logoutRedirectPath,
  securityConfig,
  onSecurityViolation,
}) => {
  const navigate = useNavigate();
  // Keep a stable reference to navigate so callbacks can have empty dependency arrays
  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);
  const [sessionId] = useState(() => crypto.randomUUID());

  // Update session manager configuration if provided
  if (securityConfig) {
    sessionManager.updateConfig(securityConfig);
  }

  const oktaAuth = useMemo(() => {
    const oktaConfig: OktaAuthOptions = {
      ...config,
      pkce: true,
      tokenManager: {
        storage: 'localStorage',
        autoRenew: true,
        autoRemove: true,
        clearPendingRemoveTokens: true,
        expireEarlySeconds: 30,
        syncStorage: true,
        ...config.tokenManager,
      },
      // Enable development mode features if in development
      ...(process.env.NODE_ENV === 'development' && {
        devMode: false,
      }),
    };

    if (enableDebugLogs) {
      console.log('Initializing OktaAuth with config:', {
        issuer: oktaConfig.issuer,
        clientId: oktaConfig.clientId,
        redirectUri: oktaConfig.redirectUri,
        scopes: oktaConfig.scopes,
      });
    }

    return new OktaAuth(oktaConfig);
  }, [config, enableDebugLogs]);

  const restoreOriginalUri = useCallback(async (_oktaAuth: OktaAuth, originalUri?: string) => {
    // NOTE: Intentionally no dependencies (see Okta issue #227). React Router v6 changes navigate identity
    // on location changes which would cause this callback to be re-created and trigger the warning in <Security />.
    const sanitizedUri = sanitizeRedirectUrl(originalUri || defaultRedirectPath);
    const targetUri = toRelativeUrl(sanitizedUri, window.location.origin);

    if (enableDebugLogs) {
      console.log('Restoring original URI (stable cb):', targetUri);
    }

    sessionManager.createSession(sessionId);
    if (enableDebugLogs) {
      console.log('Session created for timeout tracking:', { sessionId });
    }

    navigateRef.current(targetUri, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onAuthRequired = useCallback((oktaAuth: OktaAuth) => {
    // Stable callback (see comment above) to avoid repeated effect churn inside <Security />.
    if (customOnAuthRequired) {
      customOnAuthRequired(oktaAuth);
      return;
    }

    const currentPath = window.location.pathname + window.location.search;
    oktaAuth.setOriginalUri(currentPath);
    if (enableDebugLogs) {
      console.log('Auth required, saving original URI:', currentPath);
    }

    navigateRef.current(loginPath, { replace: true });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Set up global token error handling
  useEffect(() => {
    const handleTokenError = (error: TokenManagerError) => {
      if (enableDebugLogs) {
        console.error('Global token error:', error);
      }

      // Let Okta handle token validation - no need for client-side security theater

      if (onTokenError) {
        onTokenError(error);
      } else {
        // Default error handling: log error and potentially redirect to login
        console.error('Token error occurred:', {
          errorCode: error.errorCode,
          errorSummary: error.errorSummary,
          tokenKey: error.tokenKey,
        });

        // If it's a critical token error, redirect to login
        if (error.errorCode === 'invalid_token' || error.errorSummary?.includes('invalid')) {
          navigate(loginPath, { replace: true });
        }
      }
    };

    oktaAuth.tokenManager.on('error', handleTokenError);

    // Set up token renewal success logging
    if (enableDebugLogs) {
      const handleTokenRenewed = (
        key: string,
        newToken: { expiresAt: number },
        oldToken?: { expiresAt: number },
      ) => {
        console.log(`Token renewed: ${key}`, {
          newExpiry: new Date(newToken.expiresAt * 1000),
          oldExpiry: oldToken ? new Date(oldToken.expiresAt * 1000) : 'N/A',
        });
      };

      oktaAuth.tokenManager.on('renewed', handleTokenRenewed);

      return () => {
        oktaAuth.tokenManager.off('error', handleTokenError);
        oktaAuth.tokenManager.off('renewed', handleTokenRenewed);
      };
    }

    return () => {
      oktaAuth.tokenManager.off('error', handleTokenError);
    };
  }, [oktaAuth, onTokenError, navigate, enableDebugLogs, loginPath, onSecurityViolation]);

  return (
    <Security
      oktaAuth={oktaAuth}
      onAuthRequired={onAuthRequired}
      restoreOriginalUri={restoreOriginalUri}
    >
      <AuthSettingsContext.Provider
        value={{
          loginPath,
          // fallback to loginPath if logoutRedirectPath not provided
          logoutRedirectPath: logoutRedirectPath || loginPath,
          defaultRedirectPath,
        }}
      >
        {children}
      </AuthSettingsContext.Provider>
    </Security>
  );
};
